// One table for either leg, rendered from the column definitions the API serves. The row's own
// identifier leads and its creation date trails - both sortable, neither a column of the
// spreadsheet. A derived column has no stored value: its header is not a sort control, its cells
// carry the computed text, and both are marked so a reader can tell a formula from an entry.
// Every stored header also carries the column's filter (TX-US-0020); a derived header carries
// neither control.
//
// The edit and delete columns are offered only on rows the caller may change (the row's `mine`
// flag, which is OPERATIONS), and lead the table: the controls are what Operations comes to the
// tracker for, and first is where the eye starts.
//
// The total row (TX-US-0017) is the table's foot: the count where the identifier goes, a sum
// under each totalled column, nothing under the rest. It is absent until the totals arrive rather
// than shown as zeros, and it is of every row the filter admits, not of the page above it.

import AttachmentBadge from './attachments/AttachmentBadge.jsx';
import { cellClass, display } from './cells.js';
import ColumnFilter from './ColumnFilter.jsx';
import { CopyRowAction, DeleteRowAction, EditRowAction } from './RowActions.jsx';
import { ROW_COLUMNS } from './trackerUrl.js';

export default function DeliveryTable({
  leg, columns, rows, totals, query, url, onSort, onFilter, canWrite, onDelete, onAttachments,
  scrollRef,
}) {
  return (
    // The ref is how the tracker measures whether this fits; the box scrolls only in the moment
    // between being laid out and being replaced, which is before paint.
    <div className="table-scroll" ref={scrollRef}>
      <table className="data-table">
        <thead>
          <tr>
            {canWrite && <th className="plain" aria-label="Edit" />}
            {canWrite && <th className="plain" aria-label="Copy" />}
            {canWrite && <th className="plain" aria-label="Delete" />}
            <SortHeader column={ROW_COLUMNS.id} query={query} onSort={onSort} />
            <th className="plain" aria-label="Attachments" />
            {columns.map((column) => (column.derived
              ? (
                <th key={column.key} className="plain derived" title={column.note ?? undefined}>
                  {column.label}{column.note && ' *'}
                </th>
              )
              : (
                <SortHeader key={column.key} column={column} query={query} onSort={onSort}
                            leg={leg} url={url} onFilter={onFilter} />
              )))}
            <SortHeader column={ROW_COLUMNS.created} query={query} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.delivery_id}>
              {canWrite && (
                <td className="row-actions"><EditRowAction leg={leg} row={row} /></td>
              )}
              {canWrite && (
                <td className="row-actions"><CopyRowAction leg={leg} row={row} /></td>
              )}
              {canWrite && (
                <td className="row-actions"><DeleteRowAction row={row} onDelete={onDelete} /></td>
              )}
              <td><code>{row.delivery_id}</code></td>
              <td className="row-actions"><AttachmentBadge row={row} onOpen={onAttachments} /></td>
              {columns.map((column) => (column.derived
                ? <td key={column.key} className="derived">{row.derived[column.key] ?? ''}</td>
                : (
                  <td key={column.key} className={cellClass(column)}>
                    {display(column, row.values[column.key])}
                  </td>
                )))}
              <td className="when">{new Date(row.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
        {totals && (
          <tfoot>
            <tr className="totals">
              {canWrite && <td />}
              {canWrite && <td />}
              {canWrite && <td />}
              <td>{totalLabel(totals)}</td>
              <td />
              {columns.map((column) => (
                <td key={column.key} className={column.derived ? 'derived' : undefined}>
                  {totals.totals[column.key] ?? ''}
                </td>
              ))}
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export const totalLabel = (totals) =>
  `Total (${totals.count} ${totals.count === 1 ? 'row' : 'rows'})`;

function SortHeader({ column, query, onSort, leg, url, onFilter }) {
  const sorted = query.sort === column.key;
  return (
    <th aria-sort={sorted ? (query.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <span className="header-controls">
        <button type="button" onClick={() => onSort(column.key)}>
          {column.label}{sorted && (query.direction === 'asc' ? ' ↑' : ' ↓')}
        </button>
        {onFilter && (
          <ColumnFilter leg={leg} column={column} query={query} url={url} onFilter={onFilter} />
        )}
      </span>
    </th>
  );
}
